import { createFileRoute, Link } from "@tanstack/react-router";
import { Component, type ReactNode } from "react";
import { ArrowLeft, FileText, LoaderCircle, AlertTriangle } from "lucide-react";

import { ProtectedRoute } from "@/components/ProtectedRoute";
import { useVerdict } from "@/hooks/use-verdict";
import { useMemoStream } from "@/hooks/use-memo-stream";
import { NanaeHeader, NanaeFooter } from "@/components/qv3";
import { useAuth } from "@/contexts/AuthContext";
import type { VerdictClarity } from "@/components/Verdict/VerdictClarity";
import type { Branch } from "@/lib/branches";
import { VerdictAuditView } from "@/components/Verdict/VerdictAuditView";
import { VerdictVerifView } from "@/components/Verdict/VerdictVerifView";
import { VerdictShort } from "@/components/Verdict/VerdictShort";
import { apiFetch } from "@/lib/api";
import { NanaeDisclaimer } from "@/components/qorum/NanaeDisclaimer";
import { ConfidenceLCD } from "@/components/qorum/ConfidenceLCD";
import { DissidenceCard } from "@/components/qorum/DissidenceCard";
import { socialMeta } from "@/lib/seo-meta";


export const Route = createFileRoute("/verdict/$id")({
  head: ({ params }) => ({
    meta: [{ title: "Le Verdict — Hōjin" }, { name: "robots", content: "noindex, nofollow" },
      ...socialMeta({
        title: "Le Verdict — Hōjin",
        description: "Le Verdict — Hōjin",
        url: `https://hojin.ai/verdict/${params.id}`,
        image: "memo",
        card: "summary_large_image",
      }),
    ],
    links: [{ rel: "canonical", href: `https://hojin.ai/verdict/${params.id}` }],
  }),
  component: VerdictPage,
});

export function VerdictPage() {
  const { id } = Route.useParams();
  return (
    <ProtectedRoute>
      <VerdictView id={id} />
    </ProtectedRoute>
  );
}

/* ───────── Defensive boundary per section ───────── */
class SectionBoundary extends Component<
  { name: string; children: ReactNode },
  { hasError: boolean }
> {
  state = { hasError: false };
  static getDerivedStateFromError() {
    return { hasError: true };
  }
  componentDidCatch(err: unknown) {
    // eslint-disable-next-line no-console
    console.error(`[Verdict] section "${this.props.name}" a planté:`, err);
  }
  render() {
    if (this.state.hasError) return <Placeholder label={`§ ${this.props.name} — indisponible`} />;
    return this.props.children;
  }
}

const SURFACE = "rgba(255,23,68,0.05)";
const INK = "var(--qv3-ink)";
const MUTED = "var(--qv3-grey-500)";
const GOLD = "#F5EFE3";
const GOLD_SOFT = "rgba(245,239,227,0.4)";




function Placeholder({ label }: { label: string }) {
  return (
    <div
      style={{
        padding: "20px 24px",
        border: `1px dashed ${GOLD_SOFT}`,
        background: SURFACE,
        fontFamily: "var(--qv3-font-mono)",
        fontSize: 11,
        letterSpacing: 2,
        textTransform: "uppercase",
        color: MUTED,
      }}
    >
      {label}
    </div>
  );
}

/**
 * Skeleton sobre conforme à la DA Hōjin : surface or doux + barres pulsantes.
 * Affiché pendant que verdictClarity est encore en cours de persistance.
 */
function ClaritySkeleton({ lines = 3, label }: { lines?: number; label: string }) {
  return (
    <div
      aria-busy="true"
      aria-live="polite"
      style={{
        padding: "20px 24px",
        border: `1px solid ${GOLD_SOFT}`,
        background: SURFACE,
        display: "flex",
        flexDirection: "column",
        gap: 14,
      }}
    >
      <style>{`@keyframes nanae-skeleton-pulse{0%,100%{opacity:.35}50%{opacity:.7}}`}</style>
      <div
        style={{
          fontFamily: "var(--qv3-font-mono)",
          fontSize: 10,
          letterSpacing: 2,
          textTransform: "uppercase",
          color: GOLD,
        }}
      >
        {label}
      </div>
      {Array.from({ length: lines }).map((_, i) => (
        <div
          key={i}
          style={{
            height: 12,
            width: `${100 - i * 12}%`,
            background: "rgba(255,23,68,0.18)",
            animation: "nanae-skeleton-pulse 1.6s ease-in-out infinite",
            animationDelay: `${i * 0.12}s`,
          }}
        />
      ))}
    </div>
  );
}


/* ───────── Section shell ───────── */
function Section({
  index,
  title,
  children,
}: {
  index: string;
  title: string;
  children: ReactNode;
}) {
  return (
    <section style={{ marginBottom: 56 }}>
      <header
        style={{
          display: "flex",
          alignItems: "baseline",
          gap: 16,
          paddingBottom: 12,
          marginBottom: 24,
          borderBottom: `1px solid ${GOLD_SOFT}`,
        }}
      >
        <span
          style={{
            fontFamily: "var(--qv3-font-mono)",
            fontSize: 11,
            letterSpacing: 3,
            textTransform: "uppercase",
            color: GOLD,
          }}
        >
          § {index}
        </span>
        <h2
          style={{
            fontFamily: "var(--qv3-font-serif)",
            fontWeight: 400,
            fontSize: 28,
            margin: 0,
            color: INK,
            letterSpacing: "-0.01em",
          }}
        >
          {title}
        </h2>
      </header>
      {children}
    </section>
  );
}

/* ───────── 1. Hero (score + phrase) ───────── */
const SCORE_COLOR: Record<string, string> = {
  green: "#5C8C4A",
  yellow: "#000000",
  red: "#B44848",
};

function HeroSection({ clarity }: { clarity: VerdictClarity | null }) {
  const v = clarity?.verdict;
  const score = v?.score;
  const value = typeof score?.value === "number" ? score.value : null;
  const scale = typeof score?.scale === "number" ? score.scale : 10;
  const symbol = score?.symbol ?? "yellow";
  const color = SCORE_COLOR[symbol] ?? GOLD;
  const phrase = v?.phrase ?? null;
  if (value === null && !phrase) return <Placeholder label="Verdict en cours" />;

  return (
    <section
      style={{
        textAlign: "center",
        padding: "64px 24px",
        border: `1px solid ${GOLD_SOFT}`,
        marginBottom: 64,
        background: SURFACE,
      }}
    >
      {value !== null && (
        <div
          style={{
            display: "inline-flex",
            alignItems: "baseline",
            gap: 8,
            marginBottom: 28,
          }}
        >
          <span
            style={{
              fontFamily: "var(--qv3-font-serif)",
              fontWeight: 300,
              fontSize: 96,
              lineHeight: 1,
              color,
              letterSpacing: "-0.04em",
            }}
          >
            {value.toFixed(1)}
          </span>
          <span
            style={{
              fontFamily: "var(--qv3-font-mono)",
              fontSize: 14,
              color: MUTED,
              letterSpacing: 2,
            }}
          >
            / {scale}
          </span>
        </div>
      )}
      <div
        style={{
          fontFamily: "var(--qv3-font-mono)",
          fontSize: 10,
          letterSpacing: 3,
          textTransform: "uppercase",
          color: GOLD,
          marginBottom: 16,
        }}
      >
        Indice de conviction
      </div>
      {phrase && (
        <h1
          style={{
            fontFamily: "var(--qv3-font-serif)",
            fontWeight: 400,
            fontStyle: "italic",
            fontSize: "clamp(22px, 3vw, 30px)",
            lineHeight: 1.3,
            color: INK,
            margin: 0,
            maxWidth: 640,
            marginInline: "auto",
          }}
        >
          « {phrase} »
        </h1>
      )}
    </section>
  );
}

/* ───────── 2. Instrument ───────── */
function InstrumentSection({
  clarity,
  branch,
}: {
  clarity: VerdictClarity | null;
  branch: Branch | null;
}) {
  // Branche Business : indice de conviction 0-10 depuis clarity.verdict.score.value.
  if (branch === "business") {
    const score = clarity?.verdict?.score;
    const rawValue = typeof score?.value === "number" ? score.value : null;
    if (rawValue === null) {
      return <Placeholder label="Indice de conviction en cours…" />;
    }
    const v10 = rawValue <= 1 ? rawValue * 10 : rawValue;
    const clamped = Math.max(0, Math.min(10, v10));
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        <Badge label="Indice de conviction" value={`${clamped.toFixed(1)} / 10`} accent />
      </div>
    );
  }

  // Tendances (et fallback historique) : instrument {classification, reco, justification}.
  const inst = (clarity as unknown as { instrument?: { classification?: string; reco?: string; justification?: string } } | null)?.instrument;
  if (!inst || (!inst.classification && !inst.reco && !inst.justification)) {
    return <Placeholder label="Instrument non précisé" />;
  }
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div style={{ display: "flex", gap: 24, flexWrap: "wrap" }}>
        {inst.reco && (
          <Badge label="Recommandation" value={inst.reco} accent />
        )}
        {inst.classification && (
          <Badge label="Classification" value={inst.classification} />
        )}
      </div>
      {inst.justification && (
        <p
          style={{
            fontFamily: "var(--qv3-font-serif)",
            fontSize: 17,
            lineHeight: 1.65,
            color: INK,
            margin: 0,
          }}
        >
          {inst.justification}
        </p>
      )}
    </div>
  );
}

function Badge({ label, value, accent }: { label: string; value: string; accent?: boolean }) {
  return (
    <div
      style={{
        padding: "12px 20px",
        border: `1px solid ${accent ? GOLD : GOLD_SOFT}`,
        background: accent ? "var(--qv3-gold-tint)" : "transparent",
      }}
    >
      <div
        style={{
          fontFamily: "var(--qv3-font-mono)",
          fontSize: 10,
          letterSpacing: 2,
          textTransform: "uppercase",
          color: MUTED,
          marginBottom: 4,
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontFamily: "var(--qv3-font-serif)",
          fontSize: 22,
          color: accent ? GOLD : INK,
        }}
      >
        {value}
      </div>
    </div>
  );
}

/* ───────── 3. Pourquoi ───────── */
function PourquoiSection({ clarity }: { clarity: VerdictClarity | null }) {
  const p = clarity?.pourquoi;
  const summary = p?.summary ?? "";
  const points = Array.isArray(p?.consensus_points) ? p!.consensus_points! : [];
  if (!summary && points.length === 0) return <Placeholder label="Pas de synthèse" />;
  return (
    <div>
      {summary && (
        <p
          style={{
            fontFamily: "var(--qv3-font-serif)",
            fontSize: 17,
            lineHeight: 1.7,
            color: INK,
            margin: "0 0 24px",
          }}
        >
          {summary}
        </p>
      )}
      {points.length > 0 && (
        <ul
          style={{
            listStyle: "none",
            padding: "20px 24px",
            margin: 0,
            borderLeft: `2px solid ${GOLD}`,
            background: SURFACE,
          }}
        >
          {points.map((pt, i) => (
            <li
              key={i}
              style={{
                display: "flex",
                gap: 12,
                marginBottom: 10,
                fontSize: 15,
                lineHeight: 1.6,
                color: INK,
              }}
            >
              <span style={{ color: GOLD }}>—</span>
              <span>{pt}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/* ───────── 4. Recommandations ───────── */
const PRIO_LABEL: Record<string, string> = {
  critical: "Non négociable",
  high: "Très recommandé",
  medium: "Recommandé",
};

function RecommandationsSection({ clarity }: { clarity: VerdictClarity | null }) {
  const items = clarity?.recommandations ?? [];
  if (!items.length) return <Placeholder label="Aucune recommandation" />;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      {items.map((r, i) => (
        <article
          key={r.id || i}
          style={{
            padding: "20px 24px",
            border: `1px solid ${GOLD_SOFT}`,
            background: SURFACE,
          }}
        >
          <div
            style={{
              fontFamily: "var(--qv3-font-mono)",
              fontSize: 10,
              letterSpacing: 2,
              textTransform: "uppercase",
              color: GOLD,
              marginBottom: 8,
            }}
          >
            #{i + 1} · {PRIO_LABEL[r.priority] ?? r.priority}
          </div>
          <h3
            style={{
              fontFamily: "var(--qv3-font-serif)",
              fontWeight: 500,
              fontSize: 20,
              color: INK,
              margin: "0 0 12px",
            }}
          >
            {r.titre}
          </h3>
          {r.pourquoi && (
            <p
              style={{
                fontFamily: "var(--qv3-font-serif)",
                fontStyle: "italic",
                fontSize: 15,
                lineHeight: 1.6,
                color: MUTED,
                margin: "0 0 12px",
              }}
            >
              {r.pourquoi}
            </p>
          )}
          {r.action_concrete && (
            <p
              style={{
                fontFamily: "var(--qv3-font-sans)",
                fontSize: 14,
                lineHeight: 1.6,
                color: INK,
                margin: 0,
                paddingTop: 12,
                borderTop: "1px solid rgba(255,23,68,0.2)",
              }}
            >
              <span
                style={{
                  fontFamily: "var(--qv3-font-mono)",
                  fontSize: 10,
                  letterSpacing: 2,
                  textTransform: "uppercase",
                  color: GOLD,
                  marginRight: 8,
                }}
              >
                Action
              </span>
              {r.action_concrete}
            </p>
          )}
        </article>
      ))}
    </div>
  );
}

/* ───────── 5. Mises en garde ───────── */
function WarningsSection({ clarity }: { clarity: VerdictClarity | null }) {
  const items = clarity?.mises_en_garde ?? [];
  if (!items.length) return <Placeholder label="Aucune mise en garde" />;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      {items.map((w, i) => (
        <article
          key={w.id || i}
          style={{
            padding: "18px 22px",
            borderLeft: `3px solid ${w.severity === "critical" ? "#B44848" : GOLD}`,
            background: SURFACE,
          }}
        >
          <div
            style={{
              fontFamily: "var(--qv3-font-mono)",
              fontSize: 10,
              letterSpacing: 2,
              textTransform: "uppercase",
              color: w.severity === "critical" ? "#B44848" : GOLD,
              marginBottom: 6,
            }}
          >
            {w.severity === "critical" ? "Critique" : "Attention"}
          </div>
          <h3
            style={{
              fontFamily: "var(--qv3-font-serif)",
              fontWeight: 500,
              fontSize: 18,
              color: INK,
              margin: "0 0 8px",
            }}
          >
            {w.titre}
          </h3>
          {w.explication && (
            <p style={{ fontSize: 14, lineHeight: 1.6, color: MUTED, margin: "0 0 8px" }}>
              {w.explication}
            </p>
          )}
          {w.action_preventive && (
            <p style={{ fontSize: 14, lineHeight: 1.6, color: INK, margin: 0 }}>
              <span
                style={{
                  fontFamily: "var(--qv3-font-mono)",
                  fontSize: 10,
                  letterSpacing: 2,
                  textTransform: "uppercase",
                  color: GOLD,
                  marginRight: 8,
                }}
              >
                Prévention
              </span>
              {w.action_preventive}
            </p>
          )}
        </article>
      ))}
    </div>
  );
}

/* ───────── 6. Plan d'attaque ───────── */
function PlanSection({ clarity }: { clarity: VerdictClarity | null }) {
  const plan = clarity?.plan_attaque;
  if (!plan) return <Placeholder label="Pas de plan" />;
  const buckets: Array<{ key: string; label: string; items: Array<{ titre: string; cout?: string; temps?: string }> }> = [
    { key: "cette_semaine", label: "Cette semaine", items: plan.cette_semaine ?? [] },
    { key: "ce_mois", label: "Ce mois", items: plan.ce_mois ?? [] },
    { key: "mois_prochain", label: "Mois prochain", items: plan.mois_prochain ?? [] },
  ];
  const hasAny = buckets.some((b) => b.items.length > 0);
  if (!hasAny) return <Placeholder label="Pas d'actions planifiées" />;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 28 }}>
      {buckets.map((b) =>
        b.items.length > 0 ? (
          <div key={b.key}>
            <div
              style={{
                fontFamily: "var(--qv3-font-mono)",
                fontSize: 11,
                letterSpacing: 3,
                textTransform: "uppercase",
                color: GOLD,
                marginBottom: 12,
              }}
            >
              {b.label}
            </div>
            <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
              {b.items.map((a, i) => (
                <li
                  key={i}
                  style={{
                    padding: "12px 0",
                    borderBottom: "1px solid rgba(255,23,68,0.15)",
                    display: "flex",
                    gap: 16,
                    alignItems: "baseline",
                    fontSize: 15,
                    color: INK,
                  }}
                >
                  <span style={{ color: GOLD, fontFamily: "var(--qv3-font-mono)", fontSize: 12 }}>
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <span style={{ flex: 1 }}>{a.titre}</span>
                  {(a.cout || a.temps) && (
                    <span
                      style={{
                        fontFamily: "var(--qv3-font-mono)",
                        fontSize: 11,
                        color: MUTED,
                        letterSpacing: 1,
                      }}
                    >
                      {[a.temps, a.cout].filter(Boolean).join(" · ")}
                    </span>
                  )}
                </li>
              ))}
            </ul>
          </div>
        ) : null,
      )}
    </div>
  );
}

/* ───────── 7. Ressources ───────── */
function ResourcesSection({ clarity }: { clarity: VerdictClarity | null }) {
  const items = clarity?.ressources ?? [];
  if (!items.length) return <Placeholder label="Aucune ressource" />;
  return (
    <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
      {items.map((r, i) => (
        <li
          key={i}
          style={{
            padding: "14px 0",
            borderBottom: "1px solid rgba(255,23,68,0.15)",
          }}
        >
          <a
            href={r.url}
            target="_blank"
            rel="noreferrer"
            style={{
              color: INK,
              textDecoration: "none",
              display: "block",
            }}
          >
            <div
              style={{
                fontFamily: "var(--qv3-font-mono)",
                fontSize: 10,
                letterSpacing: 2,
                textTransform: "uppercase",
                color: GOLD,
                marginBottom: 4,
              }}
            >
              {r.type}
            </div>
            <div style={{ fontFamily: "var(--qv3-font-serif)", fontSize: 17, color: INK }}>
              {r.titre} ↗
            </div>
            {r.description && (
              <div style={{ fontSize: 13, color: MUTED, marginTop: 4 }}>{r.description}</div>
            )}
          </a>
        </li>
      ))}
    </ul>
  );
}

/* ───────── Top nav (back + memo) ───────── */
function TopNav({ memoId, gateMemo = false }: { memoId: string; gateMemo?: boolean }) {
  const memo = useMemoStream(memoId, gateMemo);
  // gateMemo=false → comportement legacy : bouton toujours révélé.
  const status = gateMemo ? memo.status : "ready";
  const resolvedMemoId = memo.memoId ?? memoId;

  const btnGhost: React.CSSProperties = {
    background: "transparent",
    border: `0.5px solid ${GOLD_SOFT}`,
    color: INK,
    padding: "11px 18px",
    fontFamily: "var(--qv3-font-mono)",
    fontSize: 11,
    letterSpacing: 2,
    textTransform: "uppercase",
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    textDecoration: "none",
  };
  const btnGold: React.CSSProperties = {
    background: INK,
    border: `1px solid ${GOLD}`,
    color: "var(--qv3-paper)",
    padding: "11px 18px",
    fontFamily: "var(--qv3-font-mono)",
    fontSize: 11,
    letterSpacing: 2,
    textTransform: "uppercase",
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    textDecoration: "none",
    cursor: status === "ready" ? "pointer" : status === "failed" ? "pointer" : "wait",
    opacity: status === "ready" ? 1 : 0.72,
  };

  const handleRetry = async () => {
    try {
      await apiFetch(`/api/upsell/secretaire/memo`, {
        method: "POST",
        body: JSON.stringify({ conversationId: memoId }),
      });
    } catch {
      /* l'event SSE memo_failed reprendra la main si l'erreur persiste */
    }
  };

  return (
    <div
      style={{
        display: "flex",
        flexWrap: "wrap",
        justifyContent: "space-between",
        alignItems: "center",
        gap: 12,
        marginBottom: 32,
      }}
    >
      <style>{`@keyframes memo-spin { to { transform: rotate(360deg); } }`}</style>
      <Link to="/foyer" style={btnGhost}>
        <ArrowLeft size={14} /> Retour au foyer
      </Link>
      {status === "ready" ? (
        <Link
          to="/memo/$id"
          params={{ id: resolvedMemoId }}
          state={
            memo.memoMarkdown || memo.memoHtml
              ? ({
                  memo_markdown: memo.memoMarkdown ?? undefined,
                  memo_html: memo.memoHtml ?? undefined,
                  conversation_id: memoId,
                } as never)
              : undefined
          }
          style={btnGold}
        >
          <FileText size={14} /> Voir le mémo
        </Link>
      ) : status === "failed" ? (
        <button type="button" onClick={handleRetry} style={btnGold} aria-live="polite">
          <AlertTriangle size={14} /> Mémo indisponible — réessayer
        </button>
      ) : (
        <button type="button" disabled style={btnGold} aria-live="polite">
          <LoaderCircle size={14} style={{ animation: "memo-spin 1s linear infinite" }} />{" "}
          {status === "generating" ? "Mémo en génération…" : "Mémo en préparation…"}
        </button>
      )}
    </div>
  );
}

/* ───────── Main view ───────── */
export function VerdictView({ id }: { id: string }) {
  const { user } = useAuth();
  const { data, isLoading, error, clarityPending } = useVerdict(id);
  const clarity = data?.verdictClarity ?? null;
  const conversationId = data?.conversationId ?? id;

  return (
    <div
      className="nanae-page"
      style={{ background: "var(--qv3-paper)", color: INK, minHeight: "100vh" }}
    >
      <NanaeHeader
        variant="minimal"
        title="Le Verdict"
        user={{
          email: user?.email,
          name:
            (user?.user_metadata as { first_name?: string } | undefined)?.first_name ??
            user?.email,
        }}
      />

      <main
        className="nanae-page-main"
        style={{
          paddingBottom: 120,
          maxWidth: 860,
          margin: "0 auto",
          padding: "32px 32px 120px",
        }}
      >
        {isLoading && !data ? (
          <div
            role="status"
            style={{
              fontFamily: "var(--qv3-font-serif)",
              fontStyle: "italic",
              fontSize: 20,
              color: MUTED,
              textAlign: "center",
              padding: "120px 0",
            }}
          >
            En attente du verdict…
          </div>
        ) : error ? (
          <div
            role="alert"
            style={{
              fontFamily: "var(--qv3-font-mono)",
              fontSize: 12,
              letterSpacing: 2,
              color: "#B44848",
              padding: "120px 0",
              textAlign: "center",
            }}
          >
            Erreur — {error.message}
          </div>
        ) : data ? (
          data.branch === "verif" ? (
            <>
              <TopNav memoId={conversationId} gateMemo />
              <VerdictVerifView data={data} />
            </>
          ) : clarityPending ? (
            <div
              role="status"
              style={{
                fontFamily: "var(--qv3-font-serif)",
                fontStyle: "italic",
                fontSize: 20,
                color: MUTED,
                textAlign: "center",
                padding: "120px 0",
              }}
            >
              Verdict en cours de rédaction…
            </div>
          ) : (
            <VerdictShort
              conversationId={conversationId}
              clarity={clarity}
              fallbackScore={data.confidenceScore}
              fallbackPhrase={data.verdictText}
            />
          )
        ) : null}



        {data ? (
          <NanaeDisclaimer variant={data.branch === "verif" ? "verif" : "default"} />
        ) : null}
      </main>

      <NanaeFooter variant="minimal" />
    </div>
  );
}

